/**
 * Which frame each time layer is drawing right now, shared between the time bar (the one
 * writer) and the 2D and 3D tile overlays (readers). A layer with no entry draws its live
 * `default` tiles, which is also what every layer does while the bar is on "now".
 *
 * Kept outside Redux on purpose: play swaps frames several times a second, and the
 * overlays only need to repoint their tiles, not re-render React.
 */
const frames = new Map()     // layerId -> tile template of the frame, or absent for live
// The imagery year picker's slot (NAIP, Sentinel-2): a separate writer, so the time bar
// replacing its frames never resets a picked year.
const imagery = new Map()    // layerId -> tile template of the picked year
// The live `default` tiles' cache key per layer, from its index: a re-render of the default
// frame under the same URL otherwise stays in the browser's day-long tile cache.
const live = new Map()       // layerId -> ?r= value of the live default frame
const listeners = new Set()

export function frameTemplateFor(layerId) {
  return frames.get(layerId) || imagery.get(layerId) || null
}

/** The picked year's template for an imagery layer, or null for its `default` tiles. */
export function setImageryTemplate(layerId, template) {
  if ((imagery.get(layerId) || null) === (template || null)) return
  if (template) imagery.set(layerId, template)
  else imagery.delete(layerId)
  listeners.forEach(fn => fn(new Set([layerId])))
}

/** `next` maps layerId -> template (null = live). Layers not in it go back to live. */
export function setFrameTemplates(next) {
  const changed = new Set()
  frames.forEach((template, id) => {
    if (!next.has(id) || next.get(id) !== template) changed.add(id)
  })
  next.forEach((template, id) => {
    if (template && frames.get(id) !== template) changed.add(id)
  })
  if (changed.size === 0) return
  frames.clear()
  next.forEach((template, id) => { if (template) frames.set(id, template) })
  listeners.forEach(fn => fn(changed))
}

/** `next` maps layerId -> the ?r= value of its live tiles; layers not in it lose theirs. */
export function setLiveRevisions(next) {
  const changed = new Set()
  live.forEach((r, id) => { if (next.get(id) !== r) changed.add(id) })
  next.forEach((r, id) => { if (r != null && live.get(id) !== r) changed.add(id) })
  if (changed.size === 0) return
  live.clear()
  next.forEach((r, id) => { if (r != null) live.set(id, r) })
  listeners.forEach(fn => fn(changed))
}

/** A layer's live tile template with its ?r= cache key, when its index gave one. */
export function liveTemplateFor(layerId, template) {
  const r = live.get(layerId)
  if (!template || r == null) return template
  return `${template}${template.includes('?') ? '&' : '?'}r=${encodeURIComponent(r)}`
}

export function clearFrameTemplates() {
  setFrameTemplates(new Map())
}

/** `fn(changedLayerIds)` on every change; returns the unsubscribe. */
export function subscribeFrames(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}
